// Cave84 — Wines service (Phase 1 Week 3)
// Search is client-side (230 wines, manageable). Create used by AddWine flow.

import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query,
  serverTimestamp, updateDoc, where, type QueryConstraint
} from 'firebase/firestore';
import type { Wine, WineStyle } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';
import { queueWineAnalysis } from './wineAnalysis';

const COLLECTION = 'wines';

export type WineCreateInput = Omit<Wine, 'id' | 'bottle_count' | 'vintage_count' | 'on_wishlist' | 'created_at'>;
export type WineUpdateInput = Partial<Omit<Wine, 'id' | 'bottle_count' | 'vintage_count' | 'created_at'>>;

export interface WineListOptions {
  style?: WineStyle;
  producerId?: string;
}

function col() { return typedCollection<Wine>(COLLECTION); }

export async function listWines(opts: WineListOptions = {}): Promise<Wine[]> {
  const constraints: QueryConstraint[] = [orderBy('producer_id', 'asc')];
  if (opts.style) constraints.push(where('style', '==', opts.style));
  if (opts.producerId) constraints.push(where('producer_id', '==', opts.producerId));
  const snap = await getDocs(query(col(), ...constraints));
  return snap.docs.map(d => d.data());
}

export async function getWine(id: string): Promise<Wine | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Wine, 'id'>) };
}

export async function createWine(input: WineCreateInput): Promise<Wine> {
  const payload = {
    ...input,
    bottle_count: 0,
    vintage_count: 0,
    on_wishlist: false,
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(col(), payload as unknown as Wine);
  // Auto-queue analysis for every new wine (high priority — user just added it).
  // Fire-and-forget: failure to queue doesn't block wine creation.
  queueWineAnalysis(ref.id, 'high').catch(err =>
    console.warn('[createWine] failed to queue analysis:', err)
  );
  return { id: ref.id, bottle_count: 0, vintage_count: 0, on_wishlist: false, ...input } as Wine;
}

export async function updateWine(id: string, patch: WineUpdateInput): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), patch);
}

const OWNED_STATUSES = new Set(['in_cellar', 'trading', 'virtual_pending']);

/** Thrown when a wine cannot be deleted; `message` is shown to the owner as-is. */
export class WineDeleteBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WineDeleteBlockedError';
  }
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Delete a wine's catalog entry — ONLY when nothing real hangs off it.
 *
 * Replaces deleteWineWithCascade (2026-10-21), which hard-deleted the wine with
 * every vintage and every bottle — including bottles still in the cellar — and
 * left no trace. Whole wines disappeared from the cellar that way while their
 * producers stayed behind (Confuron Musigny 2009, Hudelot-Noëllat Clos Vougeot
 * 2018, two Matrot wines; restored from the CellarTracker export).
 *
 * Refused (WineDeleteBlockedError) when the wine has any bottle — in the cellar
 * or as drinking history — or a tasting note / wishlist entry. Otherwise the
 * wine, its vintages and their critic scores are deleted, and pending AI queue
 * entries are closed so nothing is generated for a wine that no longer exists.
 */
export async function deleteEmptyWine(wineId: string): Promise<void> {
  const vintageSnap = await getDocs(query(collection(db, 'vintages'), where('wine_id', '==', wineId)));
  const vintageIds = vintageSnap.docs.map(d => d.id);
  const chunks = <T,>(xs: T[]) => Array.from({ length: Math.ceil(xs.length / 30) }, (_, i) => xs.slice(i * 30, i * 30 + 30));

  const statuses: string[] = [];
  for (const ids of chunks(vintageIds)) {
    const snap = await getDocs(query(collection(db, 'bottles'), where('vintage_id', 'in', ids)));
    snap.docs.forEach(d => statuses.push(String(d.get('status'))));
  }
  const owned = statuses.filter(s => OWNED_STATUSES.has(s)).length;
  const history = statuses.length - owned;
  if (owned) {
    throw new WineDeleteBlockedError(
      `This wine still has ${plural(owned, 'bottle')} in your cellar. Mark them as consumed, sold or gifted first — deleting the wine would erase them.`);
  }
  if (history) {
    throw new WineDeleteBlockedError(
      `This wine has ${plural(history, 'bottle')} of drinking history. Deleting it would erase that history, so it can't be deleted.`);
  }
  const [notes, wish] = await Promise.all([
    getDocs(query(collection(db, 'tasting_notes'), where('wine_id', '==', wineId))),
    getDocs(query(collection(db, 'wishlist'), where('wine_id', '==', wineId))),
  ]);
  if (!notes.empty || !wish.empty) {
    throw new WineDeleteBlockedError('This wine has tasting notes or a wishlist entry, so it can\'t be deleted.');
  }

  for (const ids of chunks(vintageIds)) {
    const scores = await getDocs(query(collection(db, 'critic_scores'), where('vintage_id', 'in', ids)));
    for (const d of scores.docs) await deleteDoc(d.ref);
  }
  for (const ids of chunks([wineId, ...vintageIds])) {
    const queue = await getDocs(query(collection(db, 'generation_queue'), where('entity_id', 'in', ids)));
    for (const d of queue.docs) {
      if (['pending', 'processing'].includes(String(d.get('status')))) {
        await updateDoc(d.ref, { status: 'failed', error_message: 'wine deleted by owner' });
      }
    }
  }
  for (const v of vintageSnap.docs) await deleteDoc(v.ref);
  await deleteDoc(doc(db, COLLECTION, wineId));
}
